import { useCallback, useEffect, useState } from 'react'
import { Flex, FlexR } from './Flex'
import { Text } from './Text'
import { VirtualTableProviderBase, TableRowData } from './IVirtualTableProvider'
import { ColumnMapper, ColumnType } from './ColumnMapper'
import { TitleDefinitionList } from './TitleDefinition'
import { LogEntry } from '@app/anylogger'
import { Cache, formatDateTime, generateRandomColor, toString } from '@app/utils'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import anylogger from '@app/anylogger'

const log = anylogger('LogEntryTableProvider')

// !NOTE: This class cannot reside in the same file as the component rendering the table when used in Next.js
// ! Next.js cannot render class components (as opposed to function components) on the server, and it mistakenly
// ! thinks that this class is being "rendered" and not just used as a data provider

export class LogEntryListViewProvider extends VirtualTableProviderBase<LogEntry> {
	columns: ColumnMapper
	allTitles: TitleDefinitionList = []

	constructor(rows: LogEntry[]) {
		super(rows)
		// const titles = this.createTitles(rows)
		const titles = ['ts', 'name', 'args']

		this.allTitles = [
			{ name: 'ts', width: '100px' },
			{ name: 'name', width: '80px' },
			{ name: 'args', autoSize: true }
		]
		let arr: ColumnType<any>[] = titles.map((title) => {
			return [title, title]
		})
		this.columns = new ColumnMapper(arr)
	}
	getAllTitles() {
		return this.allTitles
	}
	createRow(row: LogEntry, index: number) {
		// return this.columns.getValues(row)
		const timeStr = formatDateTime(row.ts, 'hh:nn:ss.zzz')
		const dateStr = formatDateTime(row.ts, 'yyyy-mm-dd')
		const color = colorCache.getValue(row.name)

		return [
			{
				value: timeStr,
				displayValue: (
					<Flex tooltip={dateStr} fixed color="gray">
						{timeStr}
					</Flex>
				)
			},
			{
				value: row.name,
				displayValue: (
					<Flex
						tooltip={row.name}
						fixed
						color={color}
						sx={{
							overflow: 'hidden'
						}}
					>
						{row.name}
					</Flex>
				)
			},
			{ value: JSON.stringify(row.args), displayValue: <ArgumentListView args={row.args} /> }
		] as TableRowData
	}
}
class ColorCache extends Cache<string, string> {
	protected retrieveValue(key: string): string {
		const col = generateRandomColor(key)
		return `rgb(${col.r}, ${col.g},${col.b})`
	}
}
const colorCache = new ColorCache()

interface LogItemViewProps {
	data: LogEntry
}
/**
 * LogItemView - Shows one row of a log entry and expands as necessary to show logged objects
 */

export function LogItemView(props: LogItemViewProps) {
	const { data } = props

	const timeStr = formatDateTime(data.ts, 'hh:nn:ss.zzz')
	const dateStr = formatDateTime(data.ts, 'yyyy-mm-dd')
	const color = colorCache.getValue(data.name)
	return (
		<FlexR alignItems="start" gap="0.25em">
			<Flex tooltip={dateStr} width="100px" fixed color="gray">
				{timeStr}
			</Flex>
			<Flex
				width="80px"
				fixed
				color={color}
				sx={{
					overflow: 'hidden'
				}}
			>
				{data.name}
			</Flex>
			<ArgumentListView args={data.args} />
		</FlexR>
	)
}

interface ArgumentListViewProps {
	args: any[]
}
/**
 * Shows all of the arguments and takes care of whether objects and arrays are expanded or not
 * @param props
 * @returns
 */
function ArgumentListView(props: ArgumentListViewProps) {
	const { args } = props
	const [expanded, setExpanded] = useState<boolean>(false)
	const [needsExpansion, setNeedsExpansion] = useState(false)
	const [debugStr, setDebugStr] = useState('')
	const [closeChildren, setCloseChildren] = useState(false)

	useEffect(() => {
		if (closeChildren) {
			setCloseChildren(false)
		}
	}, [closeChildren])
	const adjustExpanded = useCallback(
		(expanded: boolean) => {
			if (!needsExpansion) return
			if (expanded) setExpanded(true)
		},
		[needsExpansion]
	)
	const expandedClick = useCallback(() => {
		setExpanded((prev) => {
			if (prev) setCloseChildren(true)
			return !prev
		})
	}, [])

	useEffect(() => {
		let objCount = 0
		let len = 0
		for (let ii = 0; ii < args.length; ii++) {
			const arg = args[ii]
			if (typeof arg == 'object') objCount++
			else len += toString(arg).length
		}
		setDebugStr(`(${objCount}, ${len}) `)
		setNeedsExpansion(objCount > 1 || len > 80)
	}, [args])
	const getIcon = useCallback(() => {
		if (!needsExpansion) return null
		const icon = expanded ? (
			<ExpandMore color="primary" fontSize="small" />
		) : (
			<ChevronRight
				color="primary"
				fontSize="small"
				sx={{
					margin: '0px'
				}}
			/>
		)
		return (
			<Flex gap="0px" onClick={expandedClick}>
				{icon}
			</Flex>
		)
	}, [expanded, expandedClick, needsExpansion])

	return (
		<FlexR fill gap="0px" alignItems="start" overflow="hidden">
			{getIcon()}
			<Flex fill f={expanded ? 'c' : 'r'} gap="0.25em" p="0px" overflow="hidden">
				{args.map((arg: any, idx: number) => (
					<ArgumentView
						key={idx}
						arg={arg}
						index={idx}
						adjustExpanded={adjustExpanded}
						parentExpanded={expanded}
						closeChildren={closeChildren}
					/>
				))}
			</Flex>
		</FlexR>
	)
}

interface ArgumentViewProps {
	arg: any
	index: number
	adjustExpanded?: (expanded: boolean) => void
	parentExpanded?: boolean
	closeChildren?: boolean
}
/**
 * Shows one argument and expands as necessary to show logged objects
 * @param props
 * @returns
 */
function ArgumentView(props: ArgumentViewProps) {
	const { arg, index, adjustExpanded, parentExpanded, closeChildren } = props
	let res

	if (Array.isArray(arg)) {
		res = <ArrayView arr={arg} adjustExpanded={adjustExpanded} parentExpanded={parentExpanded} />
	} else if (typeof arg == 'object') {
		res = <ObjectView obj={arg} adjustExpanded={adjustExpanded} parentExpanded={parentExpanded} />
	} else res = <StringView arg={arg} parentExpanded={parentExpanded} />
	return res
}

interface StringViewProps {
	arg: any
	parentExpanded?: boolean
}
function StringView(props: StringViewProps) {
	const { arg, parentExpanded } = props

	let str = toString(arg)
	// if (str.length > 50 && !parentExpanded) str = str.slice(0, 50) + '...'
	return (
		<Text flexShrink="0" whiteSpace={parentExpanded ? '' : 'nowrap'}>
			{str}
		</Text>
	)
}
interface ArrayViewProps {
	arr: any[]
	adjustExpanded?: (expanded: boolean) => void
	parentExpanded?: boolean
}
function ArrayView(props: ArrayViewProps) {
	const { arr, adjustExpanded, parentExpanded } = props

	const [expanded, setExpanded] = useState(false)
	const changeExpanded = useCallback(
		(props: any) => {
			if (adjustExpanded) adjustExpanded(!expanded)
			setExpanded((prev) => !prev)
		},
		[adjustExpanded, expanded]
	)
	useEffect(() => {
		if (!parentExpanded) setExpanded(false)
	}, [parentExpanded])
	const getProps = useCallback(() => {
		if (!arr) return undefined
		return arr.map((val, idx) => {
			const comma = idx == 0 || expanded ? '' : ', '
			return (
				<FlexR key={idx} gap="0px" alignItems="start">
					{comma}
					<Text whiteSpace={'pre'} color="primary">
						{expanded && `${idx}: `}
					</Text>
					<ArgumentView arg={val} index={idx} parentExpanded={expanded} />
				</FlexR>
			)
		})
	}, [arr, expanded])

	const getIcon = useCallback(() => {
		const icon = expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />
		return <Flex onClick={changeExpanded}>{icon}</Flex>
	}, [changeExpanded, expanded])
	const getContent = useCallback(() => {
		if (!arr) return undefined
		const f = expanded ? 'c' : 'r'
		return (
			<Flex f={f} gap="0px" p="0px" whiteSpace="pre" sx={{ overflowX: expanded ? 'auto' : 'hidden' }}>
				{`[`}
				{getProps()}
				{`]`}
			</Flex>
		)
	}, [expanded, getProps, arr])

	return (
		<FlexR alignItems="start" gap="0px" sx={{ overflowX: expanded ? 'auto' : 'hidden' }}>
			{getIcon()}
			{getContent()}
		</FlexR>
	)
}
interface ObjectViewProps {
	obj: any
	adjustExpanded?: (expanded: boolean) => void
	parentExpanded?: boolean
}
/**
 * Displays an object and expands as necessary to show logged objects
 * @param props
 * @returns
 */
function ObjectView(props: ObjectViewProps) {
	const { obj, adjustExpanded, parentExpanded } = props

	const [expanded, setExpanded] = useState(false)
	const changeExpanded = useCallback(
		(props: any) => {
			if (adjustExpanded) adjustExpanded(!expanded)
			setExpanded((prev) => !prev)
		},
		[adjustExpanded, expanded]
	)
	useEffect(() => {
		if (!parentExpanded) setExpanded(false)
	}, [parentExpanded])

	const getProps = useCallback(() => {
		if (!obj) return undefined
		return Object.entries(obj).map(([key, val], idx) => {
			const comma = idx == 0 || expanded ? '' : ', '
			return (
				<FlexR key={idx} gap="0px" alignItems="start">
					<Text whiteSpace={'pre'} color="primary">
						{comma}
						{`${key}: `}
					</Text>
					<ArgumentView arg={val} index={idx} parentExpanded={expanded} />
				</FlexR>
			)
		})
	}, [expanded, obj])
	const getIcon = useCallback(() => {
		const icon = expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />
		return <Flex onClick={changeExpanded}>{icon}</Flex>
	}, [changeExpanded, expanded])
	const getContent = useCallback(() => {
		if (!obj) return undefined
		const f = expanded ? 'c' : 'r'
		return (
			<Flex f={f} gap="0px" p="0px" whiteSpace="pre" sx={{ overflowX: expanded ? 'auto' : 'hidden' }}>
				{`{`}
				{getProps()}
				{`}`}
			</Flex>
		)
	}, [expanded, getProps, obj])

	return (
		<FlexR alignItems="start" gap="0px" sx={{ overflowX: expanded ? 'auto' : 'hidden' }}>
			{getIcon()}
			{getContent()}
		</FlexR>
	)
}
